// src/pages/TraineeStats.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FaUserTie, FaUserTimes, FaBriefcase,
  FaSearch, FaFilter, FaTimes, FaPhone, FaEnvelope,
  FaMapMarkerAlt, FaGraduationCap, FaBuilding, FaSpinner,
  FaChevronLeft, FaChevronRight, FaCheckCircle, FaExclamationTriangle,
  FaInfoCircle, FaClock, FaMoneyBillWave, FaHeart, FaBookOpen,
  FaFileContract, FaHourglassHalf
} from 'react-icons/fa';
import axios from '../config/axiosConfig';
import { useTranslation } from '../contexts/TranslationContext';
import './TraineeStats.css';

const TraineeStats = () => {
  const { t } = useTranslation();

  // ---------- State ----------
  const [stats, setStats] = useState({
    employed_by_company: 0,
    unemployed: 0,
    professional_internship: 0,
    self_employed: 0
  });
  const [totalTrainees, setTotalTrainees] = useState(0);
  const [ongoingCount, setOngoingCount] = useState(0);
  const [loadingStats, setLoadingStats] = useState(true);

  const [departments, setDepartments] = useState([]);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [activeStatus, setActiveStatus] = useState(null);
  const [isOngoingMode, setIsOngoingMode] = useState(false);

  // Trainee list inside modal
  const [trainees, setTrainees] = useState([]);
  const [loadingTrainees, setLoadingTrainees] = useState(false);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 12, pages: 1 });

  // Contract map { woman_id: {...} }
  const [contracts, setContracts] = useState({});

  // NEW: Global Contract counts
  const [globalContractCounts, setGlobalContractCounts] = useState({ permanent: 0, short_term: 0 });
  const [loadingContracts, setLoadingContracts] = useState(false);

  // Filters
  const [filters, setFilters] = useState({ search: '', department_id: 'all' });

  // ---------- Toast system ----------
  const [toasts, setToasts] = useState([]);
  const toastIdRef = useRef(0);

  const pushToast = useCallback((message, type = 'info', duration = 3500) => {
    const id = ++toastIdRef.current;
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(x => x.id !== id)), duration);
  }, []);

  const removeToast = (id) => setToasts(prev => prev.filter(x => x.id !== id));

  const ToastIcon = ({ type }) => {
    if (type === 'success') return <FaCheckCircle />;
    if (type === 'error') return <FaExclamationTriangle />;
    return <FaInfoCircle />;
  };

  // ---------- Loaders ----------
  const fetchStats = useCallback(async () => {
    setLoadingStats(true);
    try {
      const res = await axios.get('/backend/api/public/employment-stats');
      setStats(res.data.stats || {});
      setTotalTrainees(res.data.total || 0);
    } catch (err) {
      console.error('Error loading stats:', err);
      pushToast('Failed to load statistics', 'error');
    } finally {
      setLoadingStats(false);
    }
  }, [pushToast]);

  const fetchOngoingCount = useCallback(async () => {
    try {
      const res = await axios.get('/backend/api/public/ongoing-trainees-count');
      setOngoingCount(res.data.ongoing_count || 0);
    } catch (err) {
      console.error('Error loading ongoing count:', err);
      setOngoingCount(0);
    }
  }, []);

  const fetchDepartments = useCallback(async () => {
    try {
      const res = await axios.get('/backend/api/public/departments');
      setDepartments(res.data || []);
    } catch (err) {
      console.error('Error loading departments:', err);
    }
  }, []);

  // NEW: Fetch Global Contract Counts
  const fetchGlobalContractCounts = useCallback(async () => {
    setLoadingContracts(true);
    try {
      const res = await axios.get('/backend/api/public/contract-summary');
      setGlobalContractCounts({
        permanent: res.data.permanent_count || 0,
        short_term: res.data.short_term_count || 0
      });
    } catch (err) {
      console.error('Error loading contract summary:', err);
      // Fallback to 0 if endpoint doesn't exist yet
      setGlobalContractCounts({ permanent: 0, short_term: 0 });
    } finally {
      setLoadingContracts(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    fetchOngoingCount();
    fetchDepartments();
    fetchGlobalContractCounts(); // Fetch total counts on mount
  }, [fetchStats, fetchOngoingCount, fetchDepartments, fetchGlobalContractCounts]);

  const fetchContractsFor = useCallback(async (list) => {
    if (!Array.isArray(list) || list.length === 0) return setContracts({});
    const ids = list.map(p => p.id).filter(Boolean);
    if (ids.length === 0) return setContracts({});
    try {
      const res = await axios.get('/backend/api/public/trainee-contracts', {
        params: { ids: ids.join(',') }
      });
      setContracts(res.data || {});
    } catch (err) {
      console.error('Error loading contract info:', err);
      setContracts({});
    }
  }, []);

  const fetchTrainees = useCallback(async (statusKey, page = 1) => {
    if (!statusKey && !isOngoingMode) return;
    setLoadingTrainees(true);
    try {
      const params = { page, limit: pagination.limit };
      if (filters.department_id && filters.department_id !== 'all') {
        params.department_id = filters.department_id;
      }
      if (filters.search && filters.search.trim() !== '') {
        params.search = filters.search.trim();
      }

      let url;
      if (isOngoingMode) {
        url = '/backend/api/public/ongoing-trainees';
      } else {
        url = '/backend/api/public/trainees-by-status';
        params.status = statusKey;
      }

      const res = await axios.get(url, { params });
      const list = res.data.trainees || [];
      setTrainees(list);
      setPagination(prev => ({ ...prev, ...(res.data.pagination || {}) }));

      if (statusKey === 'employed_by_company') {
        await fetchContractsFor(list);
      } else {
        setContracts({});
      }
    } catch (err) {
      console.error('Error loading trainees:', err);
      pushToast(err.response?.data?.error || 'Failed to load trainees', 'error');
      setTrainees([]);
      setContracts({});
    } finally {
      setLoadingTrainees(false);
    }
  }, [filters.department_id, filters.search, pagination.limit, pushToast, isOngoingMode, fetchContractsFor]);

  // ---------- Status cards ----------
  const statusCards = [
    { key: 'employed_by_company', label: t('employedByCompany') || 'Employed by Company',
      icon: <FaBuilding />, color: '#10b981', bg: 'rgba(16,185,129,0.1)' },
    { key: 'unemployed', label: t('unemployed') || 'Unemployed',
      icon: <FaUserTimes />, color: '#ef4444', bg: 'rgba(239,68,68,0.1)' },
    { key: 'professional_internship', label: t('Professional Internship') || 'Professional Internship',
      icon: <FaBriefcase />, color: '#3b82f6', bg: 'rgba(59,130,246,0.1)' },
    { key: 'self_employed', label: t('selfEmployed') || 'Self Employed',
      icon: <FaUserTie />, color: '#f59e0b', bg: 'rgba(245,158,11,0.1)' },
    { key: 'ongoing', label: t('Ongoing Trainings') || 'Ongoing Trainings',
      icon: <FaBookOpen />, color: '#8b5cf6', bg: 'rgba(139,92,246,0.1)', isOngoing: true }
  ];

  const handleCardClick = (card) => {
    const isOngoing = !!card.isOngoing;
    setIsOngoingMode(isOngoing);
    setActiveStatus(isOngoing ? 'ongoing' : card.key);
    setFilters({ search: '', department_id: 'all' });
    setPagination(prev => ({ ...prev, page: 1 }));
    setShowModal(true);
    setContracts({});

    setTimeout(() => {
      if (isOngoing) {
        (async () => {
          setLoadingTrainees(true);
          try {
            const res = await axios.get('/backend/api/public/ongoing-trainees', { params: { page: 1, limit: 12 } });
            setTrainees(res.data.trainees || []);
            setPagination(prev => ({ ...prev, ...(res.data.pagination || {}) }));
          } catch (err) {
            console.error(err);
            setTrainees([]);
          } finally {
            setLoadingTrainees(false);
          }
        })();
      } else {
        fetchTrainees(card.key, 1);
      }
    }, 0);

    pushToast(`Showing ${card.label}`, 'info', 2000);
  };

  const closeModal = () => {
    setShowModal(false);
    setActiveStatus(null);
    setIsOngoingMode(false);
    setTrainees([]);
    setContracts({});
  };

  useEffect(() => {
    if (showModal && (activeStatus || isOngoingMode)) {
      const timer = setTimeout(() => {
        if (isOngoingMode) {
          (async () => {
            setLoadingTrainees(true);
            try {
              const params = { page: 1, limit: pagination.limit };
              if (filters.department_id && filters.department_id !== 'all') params.department_id = filters.department_id;
              if (filters.search && filters.search.trim() !== '') params.search = filters.search.trim();
              const res = await axios.get('/backend/api/public/ongoing-trainees', { params });
              setTrainees(res.data.trainees || []);
              setPagination(prev => ({ ...prev, ...(res.data.pagination || {}) }));
            } catch (err) {
              console.error(err);
              setTrainees([]);
            } finally {
              setLoadingTrainees(false);
            }
          })();
        } else {
          fetchTrainees(activeStatus, 1);
        }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [filters.search, filters.department_id, showModal, activeStatus, isOngoingMode, fetchTrainees, pagination.limit]);

  useEffect(() => {
    document.body.style.overflow = showModal ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [showModal]);

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > pagination.pages) return;
    if (isOngoingMode) {
      (async () => {
        setLoadingTrainees(true);
        try {
          const params = { page: newPage, limit: pagination.limit };
          if (filters.department_id && filters.department_id !== 'all') params.department_id = filters.department_id;
          if (filters.search && filters.search.trim() !== '') params.search = filters.search.trim();
          const res = await axios.get('/backend/api/public/ongoing-trainees', { params });
          setTrainees(res.data.trainees || []);
          setPagination(prev => ({ ...prev, ...(res.data.pagination || {}) }));
        } catch (err) {
          console.error(err);
          setTrainees([]);
        } finally {
          setLoadingTrainees(false);
        }
      })();
    } else {
      fetchTrainees(activeStatus, newPage);
    }
  };

  const getActiveLabel = () => statusCards.find(c => c.key === activeStatus)?.label || '';
  const getActiveColor = () => statusCards.find(c => c.key === activeStatus)?.color || '#1a3a2a';

  const getEmploymentLabel = (status) => {
    const map = {
      employed_by_company: 'Employed by Company',
      unemployed: 'Unemployed',
      professional_internship: 'Professional Internship',
      self_employed: 'Self Employed',
      employed: 'Employed',
      ongoing: 'Ongoing Training'
    };
    return map[status] || status || 'Ongoing Training';
  };

  const getCardValue = (card) =>
    card.isOngoing ? (ongoingCount || 0) : (stats[card.key] || 0);

  // ---------- Contract helpers ----------
  const getContractBadge = (modeOfEmployment, contractPeriodName) => {
    const mode = (modeOfEmployment || '').toLowerCase();
    const period = (contractPeriodName || '').toLowerCase();

    if (mode.includes('permanent') || period.includes('permanent')) {
      return { label: 'Permanent', color: '#10b981', bg: 'rgba(16,185,129,0.12)' };
    }
    if (mode.includes('short') || period.includes('short')) {
      return { label: 'Short Term', color: '#f59e0b', bg: 'rgba(245,158,11,0.12)' };
    }
    if (mode.includes('intern') || period.includes('intern')) {
      return { label: 'Internship', color: '#3b82f6', bg: 'rgba(59,130,246,0.12)' };
    }
    if (mode) {
      return { label: mode.replace(/_/g, ' '), color: '#6b7280', bg: 'rgba(107,114,128,0.12)' };
    }
    return null;
  };

  const fmtDate = (d) => {
    if (!d) return null;
    try { return new Date(d).toLocaleDateString(); }
    catch { return d; }
  };

  const renderContractPanel = (person) => {
    const c = contracts[person.id] || {};

    const start  = c.contract_start_date || person.contract_start_date;
    const end    = c.contract_end_date   || person.contract_end_date;
    const mode   = c.mode_of_employment  || person.mode_of_employment;
    const period = c.contract_period;
    const days   = (typeof c.days_remaining === 'number')
      ? c.days_remaining
      : (end ? Math.ceil((new Date(end) - new Date()) / 86400000) : null);

    if (!start && !end && !mode) return null;

    const badge = getContractBadge(mode, period);
    const expiringSoon = typeof days === 'number' && days >= 0 && days <= 30;
    const expired      = typeof days === 'number' && days < 0;

    return (
      <div className="trainee-stats-contract-panel">
        <div className="trainee-stats-contract-header">
          <FaFileContract />
          <span>Contract</span>
          {badge && (
            <span className="trainee-stats-contract-badge"
                  style={{ background: badge.bg, color: badge.color }}>
              {badge.label}
            </span>
          )}
          {expiringSoon && !expired && (
            <span className="trainee-stats-contract-expiring">
              <FaHourglassHalf /> {days}d left
            </span>
          )}
          {expired && (
            <span className="trainee-stats-contract-expired">
              <FaExclamationTriangle /> Expired
            </span>
          )}
        </div>

        <div className="trainee-stats-contract-body">
          {start && (
            <div className="trainee-stats-contract-line">
              <FaClock /> Start: {fmtDate(start)}
            </div>
          )}
          {end && (
            <div className="trainee-stats-contract-line">
              <FaClock /> End: {fmtDate(end)}
            </div>
          )}
          {typeof days === 'number' && !expired && (
            <div className="trainee-stats-contract-line">
              <FaHourglassHalf /> {days} days remaining
            </div>
          )}
          {c.monthly_income && (
            <div className="trainee-stats-contract-line">
              <FaMoneyBillWave /> {c.monthly_income} RWF
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="trainee-stats-page">
      {/* Toasts */}
      <div className="trainee-stats-toasts">
        {toasts.map(toast => (
          <div key={toast.id} className={`trainee-stats-toast trainee-stats-toast-${toast.type}`}>
            <span className="trainee-stats-toast-icon"><ToastIcon type={toast.type} /></span>
            <span className="trainee-stats-toast-message">{toast.message}</span>
            <button className="trainee-stats-toast-close" onClick={() => removeToast(toast.id)}>
              <FaTimes />
            </button>
          </div>
        ))}
      </div>

      {/* Hero */}
      <section className="trainee-stats-hero">
        <div className="trainee-stats-hero-inner">
          <span className="trainee-stats-hero-tag">
            <FaHeart /> {t('ourImpact') || 'Our Impact'}
          </span>
          <h1>{t('traineeStats') || 'Trainee Statistics'}</h1>
          <p>{t('traineeStatsIntro') || 'Explore Our Certified Graduates'}</p>
          <div className="trainee-stats-hero-total">
            <span>{totalTrainees}</span>
            <small>{t('totalTrainees') || 'Certified Graduates'}</small>
          </div>
        </div>
      </section>

      {/* Cards */}
      <section className="trainee-stats-section">
        <div className="trainee-stats-container">
          {loadingStats ? (
            <div className="trainee-stats-loading">
              <FaSpinner className="trainee-stats-spin" size={40} />
              <p>{t('loading') || 'Loading...'}</p>
            </div>
          ) : (
            <div className="trainee-stats-grid trainee-stats-grid-5">
              {statusCards.map(card => (
                <button
                  key={card.key}
                  className="trainee-stats-card"
                  onClick={() => handleCardClick(card)}
                  style={{ '--card-color': card.color, '--card-bg': card.bg }}
                >
                  <div className="trainee-stats-card-icon">{card.icon}</div>
                  <div className="trainee-stats-card-body">
                    <h3>{getCardValue(card)}</h3>
                    <p>{card.label}</p>
                  </div>
                  <div className="trainee-stats-card-arrow"><FaChevronRight /></div>
                </button>
              ))}
            </div>
          )}

          <div className="trainee-stats-note">
            <FaInfoCircle />
            <p>{t('clickCardToView') || 'Click any card above to see the list of trainees in that category.'}</p>
          </div>
        </div>
      </section>

      {/* Modal */}
      {showModal && (
        <div className="trainee-stats-modal-overlay" onClick={closeModal}>
          <div className="trainee-stats-modal"
               onClick={(e) => e.stopPropagation()}
               style={{ '--modal-accent': getActiveColor() }}>
            <div className="trainee-stats-modal-header">
              <div className="trainee-stats-modal-title">
                <h2>{getActiveLabel()}</h2>
                <p>
                  {pagination.total}{' '}
                  {isOngoingMode
                    ? (t('traineesInTraining') || 'trainees in training')
                    : (t('traineesFound') || 'trainees found')}
                </p>
              </div>
              
              {/* Global Contract count badges beside the title */}
              {!isOngoingMode && activeStatus === 'employed_by_company' && (
                <div className="trainee-stats-header-contracts">
                  <span className="trainee-stats-header-badge permanent">
                    <FaBuilding /> {globalContractCounts.permanent} Permanent
                  </span>
                  <span className="trainee-stats-header-badge short-term">
                    <FaClock /> {globalContractCounts.short_term} Short Term
                  </span>
                </div>
              )}

              <button className="trainee-stats-modal-close" onClick={closeModal}>
                <FaTimes />
              </button>
            </div>

            {/* Filters */}
            <div className="trainee-stats-filters">
              <div className="trainee-stats-search">
                <FaSearch />
                <input
                  type="text"
                  placeholder={t('searchTrainees') || 'Search by name, skills...'}
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                />
                {filters.search && (
                  <button className="trainee-stats-search-clear"
                          onClick={() => setFilters(prev => ({ ...prev, search: '' }))}>
                    <FaTimes />
                  </button>
                )}
              </div>

              <div className="trainee-stats-dept-filter">
                <FaFilter />
                <select
                  value={filters.department_id}
                  onChange={(e) => setFilters(prev => ({ ...prev, department_id: e.target.value }))}
                >
                  <option value="all">{t('allDepartments') || 'All Departments'}</option>
                  {departments.map(d => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* List */}
            <div className="trainee-stats-modal-body">
              {loadingTrainees ? (
                <div className="trainee-stats-loading">
                  <FaSpinner className="trainee-stats-spin" size={36} />
                  <p>{t('loading') || 'Loading...'}</p>
                </div>
              ) : trainees.length === 0 ? (
                <div className="trainee-stats-empty">
                  <FaUserTimes size={40} />
                  <h3>{t('noTraineesFound') || 'No trainees found'}</h3>
                  <p>{t('tryDifferentFilters') || 'Try adjusting your filters or search.'}</p>
                </div>
              ) : (
                <div className="trainee-stats-trainee-grid">
                  {trainees.map(person => (
                    <div key={person.id} className="trainee-stats-trainee-card">
                      <div className="trainee-stats-trainee-info">
                        <h4>{person.name}</h4>

                        {person.department_name && (
                          <span className="trainee-stats-dept-badge">
                            <FaGraduationCap /> {person.department_name}
                          </span>
                        )}

                        {isOngoingMode && person.cohort && (
                          <span className="trainee-stats-cohort-badge">
                            <FaBookOpen /> {t('cohort') || 'Cohort'} {person.cohort}
                          </span>
                        )}

                        <span
                          className="trainee-stats-status-badge"
                          style={{ background: getActiveColor() + '22', color: getActiveColor() }}
                        >
                          {isOngoingMode
                            ? (t('ongoingTraining') || 'Ongoing Training')
                            : getEmploymentLabel(person.employment_status)}
                        </span>

                        {/* Contract panel — only when status = employed_by_company */}
                        {!isOngoingMode && person.employment_status === 'employed_by_company' &&
                          renderContractPanel(person)}

                        {isOngoingMode && (
                          <>
                            {person.enrollment_date && (
                              <div className="trainee-stats-trainee-line">
                                <FaClock /> {t('enrolled') || 'Enrolled'}: {fmtDate(person.enrollment_date)}
                              </div>
                            )}
                            {person.expected_completion_date && (
                              <div className="trainee-stats-trainee-line">
                                <FaClock /> {t('expectedCompletion') || 'Expected completion'}: {fmtDate(person.expected_completion_date)}
                              </div>
                            )}
                          </>
                        )}

                        {person.company_name && !isOngoingMode && (
                          <div className="trainee-stats-trainee-line">
                            <FaBuilding /> {person.company_name}
                            {person.working_position && ` — ${person.working_position}`}
                          </div>
                        )}

                        {person.location && (
                          <div className="trainee-stats-trainee-line">
                            <FaMapMarkerAlt /> {person.location}
                          </div>
                        )}
                        {person.phone && (
                          <div className="trainee-stats-trainee-line">
                            <FaPhone /> {person.phone}
                          </div>
                        )}
                        {person.email && (
                          <div className="trainee-stats-trainee-line trainee-stats-ellipsis">
                            <FaEnvelope /> {person.email}
                          </div>
                        )}

                        {person.age && isOngoingMode && (
                          <div className="trainee-stats-trainee-line">
                            <FaUserTie /> {t('age') || 'Age'}: {person.age}
                          </div>
                        )}
                        {person.years_of_experience > 0 && !isOngoingMode && (
                          <div className="trainee-stats-trainee-line">
                            <FaClock /> {person.years_of_experience} {t('yearsExp') || 'yrs exp'}
                          </div>
                        )}
                        {person.notes && isOngoingMode && (
                          <div className="trainee-stats-trainee-line trainee-stats-ellipsis">
                            <FaInfoCircle /> {person.notes}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="trainee-stats-pagination">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page <= 1 || loadingTrainees}
                >
                  <FaChevronLeft /> {t('previous') || 'Previous'}
                </button>
                <span>
                  {t('page') || 'Page'} {pagination.page} / {pagination.pages}
                </span>
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page >= pagination.pages || loadingTrainees}
                >
                  {t('next') || 'Next'} <FaChevronRight />
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TraineeStats;